/**
 * Utilitaire de validation des fichiers uploadés
 * Protection contre les fichiers malveillants et les uploads non autorisés
 */

export const FILE_CONSTRAINTS = {
  // Taille maximale par défaut : 20 MB
  maxSize: 20 * 1024 * 1024,

  // Types MIME autorisés pour les documents BTP
  allowedMimeTypes: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
  ],

  // Extensions autorisées
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp', '.pdf'],

  // Contraintes spécifiques par type
  avatar: {
    maxSize: 2 * 1024 * 1024, // 2 MB
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
  },

  logo: {
    maxSize: 5 * 1024 * 1024, // 5 MB
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp', '.svg'],
  },

  document: {
    maxSize: 20 * 1024 * 1024, // 20 MB
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp', '.pdf'],
  },
};

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export type FileType = 'avatar' | 'logo' | 'document';

/**
 * Valide un fichier avant upload
 * @param file - Fichier à valider
 * @param type - Type de fichier (avatar, logo, document)
 * @returns Résultat de validation avec message d'erreur si invalide
 */
export function validateFile(
  file: File,
  type: FileType = 'document'
): FileValidationResult {
  const constraints = FILE_CONSTRAINTS[type];

  // Vérifier la taille
  if (file.size > constraints.maxSize) {
    const maxMB = Math.round(constraints.maxSize / (1024 * 1024));
    const fileMB = Math.round(file.size / (1024 * 1024) * 10) / 10;
    return {
      valid: false,
      error: `Fichier trop volumineux (${fileMB} Mo). Taille maximale : ${maxMB} Mo`,
    };
  }

  // Vérifier le type MIME
  if (!constraints.allowedMimeTypes.includes(file.type)) {
    const allowedFormats = constraints.allowedExtensions
      .map(ext => ext.replace('.', '').toUpperCase())
      .join(', ');
    return {
      valid: false,
      error: `Type de fichier non autorisé. Formats acceptés : ${allowedFormats}`,
    };
  }

  // Vérifier l'extension
  const extension = getFileExtension(file.name);
  if (!constraints.allowedExtensions.includes(extension)) {
    return {
      valid: false,
      error: `Extension de fichier non autorisée : ${extension}`,
    };
  }

  return { valid: true };
}

/**
 * Extrait l'extension d'un nom de fichier en minuscules
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.slice(lastDot).toLowerCase();
}

/**
 * Génère un nom de fichier sécurisé avec UUID
 * @param originalName - Nom original du fichier
 * @returns Nom de fichier sécurisé avec UUID
 */
export function generateSafeFilename(originalName: string): string {
  const extension = getFileExtension(originalName);
  const safeExtension = FILE_CONSTRAINTS.allowedExtensions.includes(extension)
    ? extension
    : '.bin';

  return `${crypto.randomUUID()}${safeExtension}`;
}

/**
 * Sanitize le nom de fichier en retirant les caractères dangereux
 * @param filename - Nom de fichier à nettoyer
 * @returns Nom de fichier nettoyé
 */
export function sanitizeFilename(filename: string): string {
  // Retirer les caractères dangereux et garder uniquement alphanumériques, ., -, _
  return filename.replace(/[^a-zA-Z0-9._-]/g, '_');
}

/**
 * Formate la taille d'un fichier pour l'affichage
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} octets`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Calcule le hash SHA-256 d'un fichier
 * Utilisé pour le cache OCR afin d'éviter les appels API doublons
 * @param file - Fichier dont on veut calculer le hash
 * @returns Hash SHA-256 en hexadécimal (64 caractères)
 */
export async function calculateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}
